
import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    IconButton,
    Input,
    CircularProgress,
    FormControlLabel,
    Switch
} from "@mui/material";
import { Upload, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { createBlogCategory, updateBlogCategory } from "../../Services/BlogsManagement/BlogsApi";
import { useGlobalSnackbar } from "../../Components/Common/GlobalSanckbar";

const AddEditBlogCategories = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useGlobalSnackbar();
    const editData = location.state?.blog;

    const [name, setName] = useState(editData?.name || "");
    const [description, setDescription] = useState(editData?.description || "");
    const [metaTitle, setMetaTitle] = useState(editData?.metaTitle || "");
    const [metaDescription, setMetaDescription] = useState(editData?.metaDescription || "");
    const [metaKeywords, setMetaKeywords] = useState(editData?.metaKeywords || "");

    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(editData?.coverImage || null);

    const [isActive, setIsActive] = useState(editData?.isActive ?? true);

    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, coverImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setFieldErrors(prev => ({ ...prev, coverImage: "Image size must be less than 5MB" }));
                showSnackbar("Image size must be less than 5MB", "error");
                e.target.value = "";
                return;
            }

            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);

            // Clear image error when user selects a valid image
            if (fieldErrors.coverImage) {
                setFieldErrors(prev => ({ ...prev, coverImage: null }));
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (image && !image.type.startsWith("image/")) {
            setFieldErrors(prev => ({ ...prev, coverImage: "Only image files are allowed" }));
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        setLoading(true);
        setFieldErrors({});

        const formData = new FormData();
        formData.append("name", name);
        formData.append("description", description);
        formData.append("metaTitle", metaTitle);
        formData.append("metaDescription", metaDescription);
        formData.append("metaKeywords", metaKeywords);
        formData.append("isActive", isActive);

        if (image) {
            formData.append("coverImage", image);
        }

        try {
            if (editData) {
                const blogId = editData.id || editData._id;
                await updateBlogCategory(blogId, formData);
                showSnackbar("Blog category updated successfully!", "success");
            } else {
                await createBlogCategory(formData);
                showSnackbar("Blog category created successfully!", "success");
            }

            setTimeout(() => navigate("/blogs-management/categories"), 1500);
        } catch (err) {
            const responseData = err.response?.data;
            const errMsg = responseData?.message || "Failed to save blog category.";
            showSnackbar(errMsg, "error");

            // Map field-level errors if they exist in the response
            if (responseData?.details && Array.isArray(responseData.details)) {
                const errorsMap = {};
                responseData.details.forEach(item => {
                    let field = item.field;
                    if (field === "general" && item.message.toLowerCase().includes("cover image")) {
                        field = "coverImage";
                    }
                    errorsMap[field] = item.message;
                });
                setFieldErrors(errorsMap);
            } else if (responseData?.message?.toLowerCase().includes("cover image")) {
                // Manual mapping for specific message if details is null
                setFieldErrors(prev => ({ ...prev, coverImage: responseData.message }));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FBFBFB", minHeight: "100vh" }}>



            <Typography sx={{ fontSize: "1.8rem", fontWeight: 500, mb: 4 }}>
                {editData ? "Edit Blog Category" : "Add New Blog Category"}
            </Typography>

            <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #E8E8E8", maxWidth: 800, mx: "auto" }}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>

                        {/* Name Field */}
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Name
                            </Typography>
                            <TextField
                                type="text"
                                size="small"
                                fullWidth
                                placeholder="Enter category name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                error={!!fieldErrors.name}
                                helperText={fieldErrors.name}
                            />
                        </Box>

                        {/* Description */}
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Description
                            </Typography>
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                placeholder="Enter description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={!!fieldErrors.description}
                                helperText={fieldErrors.description}
                            />
                        </Box>

                        {/* Image Upload */}
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Cover Image
                            </Typography>

                            {!imagePreview ? (
                                <Box component="label"
                                    sx={{
                                        display: "block",
                                        border: "2px dashed #E0E0E0",
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" }}
                                        sx={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                    <Upload size={32} color="#888" />
                                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                                        Click to upload image
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "fit-content" }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 12, display: "block" }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveImage}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            bgcolor: "rgba(255,255,255,0.8)",
                                        }}
                                    >
                                        <X size={16} />
                                    </IconButton>
                                </Box>
                            )}
                            {fieldErrors.coverImage && (
                                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                    {fieldErrors.coverImage}
                                </Typography>
                            )}
                        </Box>

                        <Box component="hr" sx={{ my: 2, border: "none", borderTop: "1px solid #eee" }} />

                        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                            SEO Metadata
                        </Typography>

                        <TextField
                            label="Meta Title"
                            fullWidth
                            size="small"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                            error={!!fieldErrors.metaTitle}
                            helperText={fieldErrors.metaTitle}
                        />

                        <TextField
                            label="Meta Description"
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            value={metaDescription}
                            onChange={(e) => setMetaDescription(e.target.value)}
                            error={!!fieldErrors.metaDescription}
                            helperText={fieldErrors.metaDescription}
                        />

                        <TextField
                            label="Meta Keywords"
                            fullWidth
                            size="small"
                            value={metaKeywords}
                            onChange={(e) => setMetaKeywords(e.target.value)}
                            error={!!fieldErrors.metaKeywords}
                            helperText={fieldErrors.metaKeywords || "Comma separated keywords"}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                            }
                            label="Active Status"
                        />

                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/blogs-management/categories")}
                            >
                                Cancel
                            </Button>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{ bgcolor: "#FFBD65", color: "#000" }}
                            >
                                {loading
                                    ? <CircularProgress size={20} />
                                    : editData
                                        ? "Update Category"
                                        : "Create Category"}
                            </Button>
                        </Stack>

                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditBlogCategories;
